import React, { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

const badgeFor = (confidence) => {
  if (confidence >= 0.9) return { label: 'HIGH', cls: 'bg-emerald-100 text-emerald-800 border-emerald-600' };
  if (confidence >= 0.7) return { label: 'CHECK', cls: 'bg-amber-100 text-amber-900 border-amber-500' };
  return { label: 'LOW', cls: 'bg-red-100 text-red-800 border-red-500' };
};

const stateCls = {
  confirmed: 'border-l-4 border-l-emerald-500',
  corrected: 'border-l-4 border-l-amber-500',
  rejected: 'border-l-4 border-l-red-500',
};

export const ReviewableField = ({ field, state, locked, onConfirm, onCorrect, onReject }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const badge = badgeFor(field.confidence);
  const reviewed = !!state;

  const startEdit = () => {
    setDraft(String(field.value ?? ''));
    setEditing(true);
  };

  const saveEdit = () => {
    setEditing(false);
    if (draft.trim() !== '' && draft !== String(field.value ?? '')) onCorrect(field, draft);
  };

  return (
    <div
      data-testid={`review-field-${field.field_id}`}
      className={`flex items-start gap-2 border-2 border-black bg-white px-3 py-2 ${stateCls[state] || ''}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">
            {field.field_label}
          </span>
          <span className={`px-1.5 py-0 text-[9px] font-heading font-bold tracking-wider border ${badge.cls}`}>
            {badge.label}
          </span>
          {state === 'corrected' && (
            <span className="px-1.5 py-0 text-[9px] font-heading font-bold tracking-wider bg-amber-400 text-black border border-black">EDITED</span>
          )}
          {state === 'confirmed' && <Check className="w-3.5 h-3.5 text-emerald-600" />}
        </div>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(false); }}
            data-testid={`edit-input-${field.field_id}`}
            className="mt-0.5 w-full h-9 px-2 border-2 border-black font-mono text-sm focus:outline-none focus:border-primary"
          />
        ) : (
          <p className={`font-mono text-sm font-semibold truncate ${state === 'rejected' ? 'line-through text-red-500' : 'text-zinc-900'}`}>
            {String(field.value ?? '—')}
          </p>
        )}
        {field.source_span && String(field.source_span) !== String(field.value) && (
          <p className="text-[10px] text-zinc-400 truncate" title={field.source_span}>from: {field.source_span}</p>
        )}
      </div>
      {!locked && (
        <div className="flex gap-1 flex-shrink-0 pt-0.5">
          <button
            onClick={() => onConfirm(field)}
            disabled={state === 'confirmed'}
            data-testid={`confirm-${field.field_id}`}
            title="Confirm"
            className="w-9 h-9 border-2 border-black bg-white flex items-center justify-center hover:bg-emerald-500 hover:text-white disabled:bg-emerald-500 disabled:text-white transition-colors"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={startEdit}
            data-testid={`edit-${field.field_id}`}
            title="Edit"
            className="w-9 h-9 border-2 border-black bg-white flex items-center justify-center hover:bg-primary transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onReject(field)}
            disabled={state === 'rejected'}
            data-testid={`reject-${field.field_id}`}
            title="Reject"
            className="w-9 h-9 border-2 border-black bg-white flex items-center justify-center hover:bg-red-500 hover:text-white disabled:bg-red-500 disabled:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
